import React, { useEffect, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useHistory } from 'react-router-dom';
import { useFormik } from 'formik';
import isEmpty from 'lodash/isEmpty';
import styled from 'styled-components';
import isEmail from 'validator/es/lib/isEmail';
import isMobilePhone from 'validator/es/lib/isMobilePhone';
import * as Yup from 'yup';
import {
  Breadcrumbs,
  BreadcrumbsItem,
  Button,
  Empty,
  FormError,
  H3,
  Identity,
  Input,
  LayoutSidebar,
  PageActions,
  PageHeader,
  PageHeaderAction,
  PageHeaderContent,
  PageTitle,
  Section,
  Select,
  SidebarButton,
  SidebarContent,
  SidebarHeader,
  SidebarHeaderTitle,
} from 'components';
import { ICreatableSelectOption } from 'components/CreatableSelect/CreatableSelect';
import { JobTitleCreatableSelect } from 'components/JobTitleCreatableSelect/JobTitleCreatableSelect';
import { DDOption, DisbursementOptions, SiteRoleOptions, UserRoleOptions } from 'config/const';
import validations, { mobileLocale } from 'lib/Validations';
import { AddUserToSite, DuplicateUserAlert } from 'modals';
import Routes from 'routes/routeConstants';
import { clearRequest, selectAPIRequestErrors, selectAPIRequestStatus, selectIsLoadingAPIRequest } from 'store/ApiClient';
import { selectCurrentEmployee } from 'store/App';
import { addEmployeeRequest, addEmployeeToSiteRequest, Employee, updateEmployeeSiteRoleRequest, updateOrgEmployeeTypeRequest } from 'store/Employee';
import { getEmployerSitesRequest, selectCurrentEmployeeAdminSites, selectCurrentEmployeeOrg } from 'store/Employers';
import { IJobTitleState, IStore } from 'store/types';
import { EmployeeRole, EmployeeStatus, EmployeeType, IApiErrorPayloadWithCode, IEmployee, IEmployerSite, ISite, IUpdateEmployeeSite, RequestStatus, SiteRole } from 'types';
import { formatJobTitle, getAvailableEmployerSites, getDefaultDisbursementMethod, isEmployeeIdRequired, isSiteRole, stripCountryCode, stripSpaces } from 'utils';
import AddExistingUser from './AddExistingUser';
import { AddedUserListItem, AddedUsersList, Footer, Form, FormLayout, StyledFieldWrapper } from './styles';

interface UpdateSiteForm {
  employeeId?: string;
  site?: DDOption;
  role?: DDOption;
  jobTitle?: ICreatableSelectOption<IJobTitleState>;
  disbursementMethod?: DDOption;
  status?: EmployeeStatus;
}

interface AddEmployeeForm extends UpdateSiteForm {
  firstName: string;
  lastName: string;
  inviteRecipient: string;
}

const EmptyIconContainer = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 100%;
  height: 80vh;
`;

const StyledFormError = styled(FormError)`
  text-align: left;
  max-width: 80%;
`;

interface IAddEmployee extends IEmployee {
  recentlyAddedSiteId?: string;
}

type ModalTypes = undefined | 'selectUser' | 'addUserToSite' | 'duplicateEmployee';

const buildEmployeeSite = (values: UpdateSiteForm) => ({
  id: values.site.value,
  role: values.role?.value === EmployeeRole.SITE_ADMIN ? EmployeeRole.ADMIN : values.role?.value,
  job: { title: values.jobTitle?.value?.title },
  disbursementMethod: values.disbursementMethod.value,
  ...(!isEmpty(values.employeeId) && { employeeId: values.employeeId }),
  ...(!isEmpty(values.status) && { status: values.status }),
});

const UsersAdd: React.FC = () => {
  const dispatch = useDispatch();
  const history = useHistory();
  const [modalActive, setModalActive] = useState<boolean>(false);
  const [modalType, setModalType] = useState<ModalTypes>(undefined);
  const [sidebarExpanded, setSidebarExpanded] = useState<boolean>(false);
  const [existingUserToAdd, setExistingUserToAdd] = useState<IAddEmployee | null>(null);
  const pageSize = 4;
  const [name, setName] = useState<string>('');
  const [limit, setLimit] = useState<number>(pageSize);
  const [addedUsers, setAddedUsers] = useState<IAddEmployee[]>([]);
  const addNewEmployee = (emp: IEmployee, siteId?: string) => {
    setAddedUsers((employees) => [...employees, { ...emp, recentlyAddedSiteId: siteId }]);
  };
  const [duplicateEmployee, setDuplicateEmployee] = useState<IEmployee>();
  const [sitesWithSettings, setSitesWithSettings] = useState<ISite[]>();

  const currentOrganizationId = useSelector(selectCurrentEmployee())?.organizationId;
  const apiErrors = useSelector((state: IStore) => selectAPIRequestErrors(state, Employee.ADD_EMPLOYEE_REQUEST)) as IApiErrorPayloadWithCode;
  const isAddUserLoading = useSelector((state: IStore) => selectIsLoadingAPIRequest(state, Employee.ADD_EMPLOYEE_REQUEST));
  const isAddEmployeeToSiteLoading = useSelector((state: IStore) => selectIsLoadingAPIRequest(state, Employee.ADD_EMPLOYEE_TO_SITE_REQUEST));
  const isUpdateEmployeeLoading = useSelector((state: IStore) => selectIsLoadingAPIRequest(state, Employee.UPDATE_EMPLOYEE_REQUEST));
  const isUpdateExistEmployeeToSiteLoading = useSelector((state: IStore) => selectIsLoadingAPIRequest(state, Employee.UPDATE_EMPLOYEE_SITE_ROLE_REQUEST));
  const addSiteApiErrors = useSelector((state: IStore) => selectAPIRequestErrors(state, Employee.ADD_EMPLOYEE_TO_SITE_REQUEST)) as IApiErrorPayloadWithCode;
  const addExistingUserStatus = useSelector((state: IStore) => selectAPIRequestStatus(state, Employee.ADD_EMPLOYEE_TO_SITE_REQUEST));
  const updateExistingUserStatus = useSelector((state: IStore) => selectAPIRequestStatus(state, Employee.UPDATE_EMPLOYEE_SITE_ROLE_REQUEST));

  const currentEmployeeOrg = useSelector(selectCurrentEmployeeOrg);

  useEffect(() => {
    if (currentOrganizationId) {
      dispatch(getEmployerSitesRequest(currentOrganizationId, (res) => setSitesWithSettings(res.data)));
    }
  }, [currentOrganizationId]);

  const validationSchema = Yup.object().shape({
    firstName: validations.firstNameValidationSchema,
    lastName: validations.lastNameValidationSchema,
    inviteRecipient: Yup.string().test('IsIdentity', 'Invalid email address or mobile number', (identity: string) => !isEmpty(identity) && (isEmail(identity || '') || isMobilePhone(identity || '', mobileLocale))),
    ...validations.employeeSiteValidationSchema(sitesWithSettings),
  });
  const sites = useSelector(selectCurrentEmployeeAdminSites);
  const orgSites =
    sites
      ?.filter((s) => s.role === SiteRole.ADMIN)
      .map((site: IEmployerSite) => ({
        label: site.name,
        value: site.id,
      })) || [];
  const formikForm = useFormik({
    initialValues: {
      firstName: '',
      lastName: '',
      inviteRecipient: '',
      role: {
        label: '',
        value: '',
      },
      site: {
        label: '',
        value: '',
      },
      employeeId: '',
      jobTitle: {
        label: '',
        value: {
          title: '',
        } as IJobTitleState,
      },
      disbursementMethod: { label: '', value: '' },
    },
    onSubmit: (values: AddEmployeeForm) => {
      const payload = {
        firstName: stripSpaces(values.firstName),
        lastName: stripSpaces(values.lastName),
        inviteRecipient: stripCountryCode(values.inviteRecipient),
        organizationId: currentOrganizationId,
        type: EmployeeType.ADMIN,
        ...(isSiteRole(values?.role?.value as EmployeeRole) && {
          type: EmployeeType.USER,
          sites: [buildEmployeeSite(values)],
        }),
      };
      dispatch(
        addEmployeeRequest(payload, {
          onSuccess: (emp: IEmployee) => addNewEmployee(emp),
          onDuplicate: (dupEmp: IEmployee) => {
            setDuplicateEmployee(dupEmp);
            setModalActive(true);
            setModalType('duplicateEmployee');
          },
        })
      );
    },
    validateOnBlur: true,
    validationSchema,
    enableReinitialize: true,
  });
  const { errors, touched, isValid, dirty, handleSubmit, handleBlur, getFieldProps, resetForm, setFieldValue, values } = formikForm;

  const validateField = (field: 'firstName' | 'lastName' | 'inviteRecipient' | 'role' | 'site' | 'jobTitle' | 'disbursementMethod' | 'employeeId') => Boolean(touched[field] && errors[field]);

  const updateExistingEmployeeSite = (payload: IUpdateEmployeeSite, employeeId: string, siteId: string) => {
    dispatch(
      updateEmployeeSiteRoleRequest(employeeId, siteId, payload, {
        successMessage: 'Employee added to site successfully',
        onSuccess: (emp: IEmployee) => addNewEmployee(emp, siteId),
      })
    );
  };

  const submitAddToSite = (values: UpdateSiteForm, employeeId: string) => {
    const payload = buildEmployeeSite(values);
    dispatch(addEmployeeToSiteRequest(employeeId, payload, { onSuccess: (emp: IEmployee) => addNewEmployee(emp, values.site.value) }));
  };

  const addUserButtonDisabled = useMemo(() => isAddUserLoading || !(isValid && dirty) || (isSiteRole(values.role?.value as EmployeeRole) && !values.jobTitle?.value?.title), [isValid, dirty, formikForm.values]);

  const addToSiteValidateField = (field: 'role' | 'jobTitle' | 'employeeId' | 'site' | 'disbursementMethod') => Boolean(AddExistingUserFormik.touched[field] && AddExistingUserFormik.errors[field]);

  const AddExistingUserFormik = useFormik({
    initialValues: {
      role: { label: '', value: '' },
      jobTitle: {
        label: '',
        value: {
          title: '',
        } as IJobTitleState,
      },
      site: { label: '', value: '' },
      disbursementMethod: { label: '', value: '' },
      employeeId: '',
    },
    onSubmit: (values: UpdateSiteForm) => {
      // add employee to a site
      if (existingUserToAdd) {
        if (values.role.value === EmployeeRole.ADMIN) {
          dispatch(
            updateOrgEmployeeTypeRequest(existingUserToAdd.id, (emp: IEmployee) => {
              addNewEmployee(emp);
              closeModal();
            })
          );
        } else {
          const existingSite = existingUserToAdd.sites.find((s) => s.id === values.site.value);
          if (existingSite) {
            const payload = buildEmployeeSite({ ...values, status: existingUserToAdd.status });
            updateExistingEmployeeSite(payload, existingUserToAdd.id, values.site.value);
          } else {
            submitAddToSite(values, existingUserToAdd.id);
          }
        }
      }
    },
    validateOnBlur: true,
    validationSchema: Yup.object().shape({
      ...validations.employeeSiteValidationSchema(sitesWithSettings),
    }),
    enableReinitialize: true,
  });

  // Modals
  const closeModal = () => {
    AddExistingUserFormik.resetForm();
    setModalActive(false);
    setModalType(undefined);
    setName('');
    setLimit(pageSize);
  };
  const openSelectUser = () => {
    setModalActive(true);
    setModalType('selectUser');
    resetForm();
  };
  const openAddUserToSite = (emp: IEmployee) => {
    if (emp) {
      setExistingUserToAdd(emp);
      setModalActive(true);
      setModalType('addUserToSite');
    }
  };

  useEffect(() => {
    if (addExistingUserStatus === RequestStatus.SUCCESS || updateExistingUserStatus === RequestStatus.SUCCESS) {
      dispatch(clearRequest(Employee.ADD_EMPLOYEE_TO_SITE_REQUEST));
      dispatch(clearRequest(Employee.UPDATE_EMPLOYEE_SITE_ROLE_REQUEST));
      closeModal();
    }
  }, [addExistingUserStatus, updateExistingUserStatus, dispatch]);

  // reset form when a user has been added
  useEffect(() => {
    resetForm();
    AddExistingUserFormik.resetForm();
  }, [addedUsers.length]);

  return (
    <>
      <LayoutSidebar
        content={
          <>
            <PageHeader data-cy="page-header">
              <Breadcrumbs>
                <BreadcrumbsItem label="Users" to={Routes.private.employees} />
                <BreadcrumbsItem label="Add User" to={Routes.private.addEmployee} />
              </Breadcrumbs>

              <PageHeaderContent>
                <PageTitle title="Add User" />

                <PageActions mobileMarginTop>
                  <PageHeaderAction label="Add Existing" type="button" appearance="tertiary" onClick={openSelectUser} data-cy="add-existing-btn" />
                </PageActions>
              </PageHeaderContent>
            </PageHeader>

            <Section>
              <Form onSubmit={handleSubmit}>
                <FormLayout data-cy="page-form">
                  <StyledFieldWrapper data-cy="first-name-field" message={validateField('firstName') ? errors.firstName : ''} hasError={validateField('firstName')} label="First Name">
                    <Input data-cy="first-name-input" id="firstName" type="text" disabled={isAddUserLoading} placeholder="John" {...getFieldProps('firstName')} />
                  </StyledFieldWrapper>

                  <StyledFieldWrapper data-cy="last-name-field" message={validateField('lastName') ? errors.lastName : ''} hasError={validateField('lastName')} label="Last Name">
                    <Input data-cy="last-name-input" id="lastName" type="text" disabled={isAddUserLoading} placeholder="Doe" {...getFieldProps('lastName')} />
                  </StyledFieldWrapper>

                  <StyledFieldWrapper data-cy="identity-field" message={validateField('inviteRecipient') ? errors.inviteRecipient : ''} hasError={validateField('inviteRecipient')} label="Invite Method">
                    <Identity
                      data-cy="identity-input"
                      disabled={isAddUserLoading}
                      onBlur={handleBlur}
                      value={values.inviteRecipient}
                      onAccept={(val: any) => {
                        setFieldValue('inviteRecipient', val);
                      }}
                      type="text"
                      id="inviteRecipient"
                    />
                  </StyledFieldWrapper>

                  <StyledFieldWrapper data-cy="role-field" label="Role" hasError={validateField('role')}>
                    <Select
                      data-cy="role-input"
                      disabled={isAddUserLoading}
                      placeholder="Select Role"
                      options={currentEmployeeOrg?.type === EmployeeType.ADMIN ? UserRoleOptions : SiteRoleOptions}
                      onChange={(selected) => {
                        setFieldValue('role', selected?.[0]);
                      }}
                      values={[values.role]}
                    />
                  </StyledFieldWrapper>

                  {isSiteRole(values.role?.value as EmployeeRole) && (
                    <>
                      {!isEmpty(orgSites) && (
                        <StyledFieldWrapper data-cy="site-field" label="Site" hasError={validateField('site')}>
                          <Select
                            data-cy="site-input"
                            disabled={isAddUserLoading}
                            placeholder="Select Site"
                            options={orgSites}
                            onChange={(selected) => {
                              const site = selected?.[0];
                              setFieldValue('site', site);
                              setFieldValue('disbursementMethod', getDefaultDisbursementMethod(site?.value, sitesWithSettings), false);
                            }}
                            values={[values.site]}
                          />
                        </StyledFieldWrapper>
                      )}
                      <StyledFieldWrapper data-cy="job-title-field" label="Job Title" message={validateField('jobTitle') ? (errors.jobTitle as string) : ''} hasError={validateField('jobTitle')}>
                        <JobTitleCreatableSelect
                          createNewLabel="Add {search} as a new job title"
                          disabled={!values.site?.value || isAddUserLoading}
                          placeholder="Select Job Title"
                          siteId={values.site?.value}
                          onChange={(selected) => {
                            setFieldValue('jobTitle', selected);
                          }}
                          onInputBlur={handleBlur('jobTitle')}
                        />
                      </StyledFieldWrapper>
                      <StyledFieldWrapper
                        data-cy="disbursement-field"
                        label="Disbursement Method"
                        hasError={validateField('disbursementMethod')}
                        tooltip={
                          <>
                            <b>Instant</b> {' payments will be paid out instantly. '}
                            <b>Payroll</b> {" payments will be sent to your site's payroll system."}
                          </>
                        }
                        id="disbursement"
                      >
                        <Select
                          data-cy="disbursement-input"
                          disabled={isAddUserLoading}
                          placeholder="Select Disbursement Method"
                          options={DisbursementOptions}
                          onChange={(selected) => {
                            setFieldValue('disbursementMethod', selected?.[0]);
                          }}
                          values={[values.disbursementMethod]}
                        />
                      </StyledFieldWrapper>
                      <StyledFieldWrapper
                        label="Employee ID"
                        data-cy="employee-id-field"
                        optional={!isEmployeeIdRequired(values.site?.value, sitesWithSettings)}
                        message={validateField('employeeId') ? errors.employeeId : ''}
                        hasError={validateField('employeeId')}
                      >
                        <Input data-cy="employee-id-input" {...getFieldProps('employeeId')} id="employeeId" type="text" placeholder="Enter an Employee ID" maxLength={50} disabled={isAddUserLoading} />
                      </StyledFieldWrapper>
                    </>
                  )}
                </FormLayout>

                <Footer data-cy="page-footer">
                  <StyledFieldWrapper>
                    {apiErrors?.errorMessages &&
                      !apiErrors.id &&
                      apiErrors.errorMessages.map((error, index) => (
                        <StyledFormError key={`error-message-${index}`} data-cy="form-error">
                          {error}
                        </StyledFormError>
                      ))}
                  </StyledFieldWrapper>

                  <Button data-cy="submit-add-user" label="Add User" type="submit" appearance="primary" size="large" disabled={addUserButtonDisabled} isProcessing={isAddUserLoading} />
                </Footer>
              </Form>
            </Section>
          </>
        }
        sidebar={
          <>
            <SidebarHeader data-cy="side-page-header">
              <SidebarHeaderTitle>
                <SidebarButton isOpen={sidebarExpanded} onClick={() => setSidebarExpanded(!sidebarExpanded)} />
                <H3>Added ({addedUsers.length})</H3>
              </SidebarHeaderTitle>
            </SidebarHeader>

            <SidebarContent data-cy="side-page-content">
              <AddedUsersList>
                {addedUsers.length > 0 ? (
                  [...addedUsers].reverse().map((user) => {
                    let site = user.sites?.[0];
                    if (user.sites?.length > 1) {
                      site = (user.sites || []).find((s) => s.id === user.recentlyAddedSiteId) || site;
                    }
                    return (
                      <AddedUserListItem
                        key={`user-${user.id}-${user.type === EmployeeType.ADMIN ? 'org-admin' : site.id}`}
                        name={user.name}
                        type={user.type}
                        role={site?.role}
                        jobTitle={site?.job && formatJobTitle(site?.job.title)}
                        site={site?.name}
                      />
                    );
                  })
                ) : (
                  <EmptyIconContainer>
                    <Empty icon="People" message="Added users will appear here." />
                  </EmptyIconContainer>
                )}
              </AddedUsersList>
            </SidebarContent>
          </>
        }
        sidebarOpen={sidebarExpanded}
      />

      {modalActive && (
        <>
          {modalType === 'selectUser' && <AddExistingUser active={modalActive} onSelect={openAddUserToSite} onClose={closeModal} limit={limit} setLimit={setLimit} name={name} setName={setName} />}
          {modalType === 'addUserToSite' && (
            <AddUserToSite
              apiErrors={addSiteApiErrors}
              avatarUrl={existingUserToAdd?.user?.avatarUrl}
              dirty={AddExistingUserFormik.dirty}
              errors={AddExistingUserFormik.errors}
              fullName={existingUserToAdd?.name || ''}
              getFieldProps={AddExistingUserFormik.getFieldProps}
              handleBlur={AddExistingUserFormik.handleBlur}
              hasPaymentMethod={false}
              isActive={modalActive}
              isLoading={isAddEmployeeToSiteLoading || isUpdateExistEmployeeToSiteLoading || isUpdateEmployeeLoading}
              isSiteRole={isSiteRole(AddExistingUserFormik.values.role?.value as EmployeeRole)}
              isValid={AddExistingUserFormik.isValid}
              onClose={() => {
                AddExistingUserFormik.resetForm();
                dispatch(clearRequest(Employee.ADD_EMPLOYEE_TO_SITE_REQUEST));
                closeModal();
              }}
              onSubmit={AddExistingUserFormik.handleSubmit}
              setFieldTouched={AddExistingUserFormik.setFieldTouched}
              setFieldValue={AddExistingUserFormik.setFieldValue}
              sites={sites && getAvailableEmployerSites(sites, existingUserToAdd)}
              sitesWithSettings={sitesWithSettings}
              validateField={addToSiteValidateField}
              values={AddExistingUserFormik.values}
            />
          )}
          {modalType === 'duplicateEmployee' && (
            <DuplicateUserAlert
              employee={duplicateEmployee}
              employeeType={isSiteRole(values.role?.value as EmployeeRole) ? EmployeeType.USER : EmployeeType.ADMIN}
              site={values.site}
              addUserToSite={(empId: string) => submitAddToSite(values, empId)}
              activateUserSite={(empId: string, siteId: string) => updateExistingEmployeeSite({ status: EmployeeStatus.APPROVED }, empId, siteId)}
              goToUser={(dupUser) => {
                closeModal();
                history.push(`/users/${dupUser.id}`);
              }}
              onClose={closeModal}
              isActive={modalActive}
            />
          )}
        </>
      )}
    </>
  );
};

export default UsersAdd;
